import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Plus, Pencil, Check, Baby } from "lucide-react";
import { toast } from "sonner";

const AVATARS = [
  { bg: "from-red-600 to-red-800", emoji: "D" },
  { bg: "from-blue-500 to-indigo-700", emoji: "S" },
  { bg: "from-amber-500 to-orange-700", emoji: "M" },
  { bg: "from-emerald-500 to-teal-700", emoji: "K" },
  { bg: "from-purple-500 to-fuchsia-700", emoji: "X" },
  { bg: "from-pink-500 to-rose-700", emoji: "L" },
];

export default function ProfileSelect() {
  const { user, refresh, toggleKidsMode } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newKids, setNewKids] = useState(false);

  if (!user) return null;

  const pickProfile = (p) => {
    localStorage.setItem("djino_profile", JSON.stringify(p));
    toggleKidsMode(p.isKidsProfile);
    navigate(p.isKidsProfile ? "/kids" : "/browse");
  };

  const addProfile = async () => {
    if (!newName.trim()) return;
    await api.post("/profiles", { name: newName.trim(), isKidsProfile: newKids });
    toast.success("Profile created");
    setAdding(false);
    setNewName("");
    setNewKids(false);
    refresh();
  };

  const deleteProfile = async (id) => {
    if (user.profiles.length <= 1) return toast.error("You must keep at least one profile");
    await api.delete(`/profiles/${id}`);
    toast.success("Profile removed");
    refresh();
  };

  const profiles = user.profiles || [];
  const colorFor = (i) => AVATARS[i % AVATARS.length];

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 py-12 relative" data-testid="profile-select-page">
      <div className="absolute inset-0 bg-gradient-to-b from-[#1a0303]/30 via-transparent to-transparent pointer-events-none" />
      <div className="relative w-full max-w-4xl text-center">
        <h1 className="text-4xl sm:text-6xl font-black tracking-tighter mb-3 text-white" style={{ fontFamily: "Outfit" }}>
          {editing ? "Manage Profiles" : "Who's watching?"}
        </h1>
        <p className="text-[#888] mb-12">{editing ? "Tap a profile to remove or add a new one." : "Pick a profile to start streaming."}</p>

        <div className="flex flex-wrap items-start justify-center gap-6 sm:gap-10 mb-10">
          {profiles.map((p, i) => {
            const color = colorFor(i);
            const isKids = p.isKidsProfile;
            return (
              <div key={p.id} className="group relative" data-testid={`profile-${p.id}`}>
                <button
                  onClick={() => editing ? deleteProfile(p.id) : pickProfile(p)}
                  className="block transition-all hover:scale-105"
                >
                  <div className={`w-28 h-28 sm:w-36 sm:h-36 rounded-2xl bg-gradient-to-br ${isKids ? "from-blue-500 to-yellow-400" : color.bg} flex items-center justify-center text-white text-5xl font-black shadow-xl group-hover:ring-4 group-hover:ring-white/40 transition-all relative`} style={{ fontFamily: "Outfit" }}>
                    {isKids ? <Baby size={48} /> : (p.name?.[0]?.toUpperCase() || color.emoji)}
                    {editing && (
                      <div className="absolute inset-0 bg-black/70 rounded-2xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                        <Pencil size={28} />
                      </div>
                    )}
                  </div>
                  <div className="mt-3 text-sm font-semibold text-white group-hover:text-[#E50914] transition">{p.name}</div>
                  {isKids && <div className="text-[10px] text-[#888] uppercase tracking-wider mt-1">Kids</div>}
                </button>
              </div>
            );
          })}

          {!editing && profiles.length < 5 && (
            <button onClick={() => setAdding(true)} className="block group" data-testid="add-profile-btn">
              <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-2xl bg-[#1a1a1a] border border-white/10 flex items-center justify-center text-[#888] group-hover:bg-[#2a2a2a] group-hover:border-[#E50914] group-hover:text-[#E50914] transition-all">
                <Plus size={48} />
              </div>
              <div className="mt-3 text-sm font-semibold text-[#888] group-hover:text-[#E50914]">Add Profile</div>
            </button>
          )}
        </div>

        <button
          onClick={() => setEditing((v) => !v)}
          className="text-sm text-[#888] hover:text-white border border-white/15 px-5 py-2 rounded-md transition"
          data-testid="manage-profiles-btn"
        >
          {editing ? "Done" : "Manage Profiles"}
        </button>
      </div>

      {adding && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 w-full max-w-md" data-testid="add-profile-modal">
            <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: "Outfit" }}>New Profile</h2>
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Profile name"
              className="w-full px-4 py-3 rounded-md bg-[#1a1a1a] border border-white/10 text-white focus:border-[#E50914] focus:outline-none mb-4"
              data-testid="profile-name-input"
            />
            <label className="flex items-center gap-2 text-sm cursor-pointer mb-5">
              <input type="checkbox" checked={newKids} onChange={(e) => setNewKids(e.target.checked)} className="w-4 h-4 accent-[#E50914]" />
              <Baby size={16} className="text-[#3B82F6]" /> This is a kids profile
            </label>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setAdding(false)} className="btn-secondary !py-2 !px-4">Cancel</button>
              <button onClick={addProfile} className="btn-primary !py-2 !px-4" data-testid="save-profile-btn"><Check size={16} /> Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
